import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { PatFormData } from 'components/Assessments/PatAssessment/types/PatFormData'
import { PatAssessmentQuestionnaireItem } from 'components/Assessments/PatAssessment/types/questionnaire'
import { CodingFieldAdapter } from 'components/Assessments/PatAssessment/adapters/CodingFieldAdapter'
import { BradenScaleFormField } from './formField'

/**
 * Braden Scale subsection adapter for PAT Assessment
 * Handles Braden Scale extraction and transformation
 */
export class BradenScaleAdapter extends CodingFieldAdapter {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<PatFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Extract coding values for questions using helper method
    const sensory = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_SENSORY,
      false
    )

    const moisture = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_MOISTURE,
      false
    )

    const activity = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_ACTIVITY,
      false
    )

    const mobility = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_MOBILITY,
      false
    )

    const nutrition = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_NUTRITION,
      false
    )

    const frictionAndShear = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_FRICTION_AND_SHEAR,
      false
    )

    // Extract notes fields using helper method
    const sensoryNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_SENSORY_NOTES
    )

    const moistureNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_MOISTURE_NOTES
    )

    const activityNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_ACTIVITY_NOTES
    )

    const mobilityNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_MOBILITY_NOTES
    )

    const nutritionNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_NUTRITION_NOTES
    )

    const frictionAndShearNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_FRICTION_AND_SHEAR_NOTES
    )

    return {
      [BradenScaleFormField.BRADEN_SCALE_SENSORY_QUESTION]: sensory,
      [BradenScaleFormField.BRADEN_SCALE_SENSORY_NOTES]: sensoryNotes,
      [BradenScaleFormField.BRADEN_SCALE_MOISTURE_QUESTION]: moisture,
      [BradenScaleFormField.BRADEN_SCALE_MOISTURE_NOTES]: moistureNotes,
      [BradenScaleFormField.BRADEN_SCALE_ACTIVITY_QUESTION]: activity,
      [BradenScaleFormField.BRADEN_SCALE_ACTIVITY_NOTES]: activityNotes,
      [BradenScaleFormField.BRADEN_SCALE_MOBILITY_QUESTION]: mobility,
      [BradenScaleFormField.BRADEN_SCALE_MOBILITY_NOTES]: mobilityNotes,
      [BradenScaleFormField.BRADEN_SCALE_NUTRITION_QUESTION]: nutrition,
      [BradenScaleFormField.BRADEN_SCALE_NUTRITION_NOTES]: nutritionNotes,
      [BradenScaleFormField.BRADEN_SCALE_FRICTION_AND_SHEAR_QUESTION]: frictionAndShear,
      [BradenScaleFormField.BRADEN_SCALE_FRICTION_AND_SHEAR_NOTES]: frictionAndShearNotes
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    // Build coding value mutations using helper method
    const sensoryMutation = this.buildSingleCodingValueMutation(
      BradenScaleFormField.BRADEN_SCALE_SENSORY_QUESTION,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_SENSORY,
      false
    )
    if (sensoryMutation) {
      mutations.push(sensoryMutation)
    }

    const moistureMutation = this.buildSingleCodingValueMutation(
      BradenScaleFormField.BRADEN_SCALE_MOISTURE_QUESTION,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_MOISTURE,
      false
    )
    if (moistureMutation) {
      mutations.push(moistureMutation)
    }

    const activityMutation = this.buildSingleCodingValueMutation(
      BradenScaleFormField.BRADEN_SCALE_ACTIVITY_QUESTION,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_ACTIVITY,
      false
    )
    if (activityMutation) {
      mutations.push(activityMutation)
    }

    const mobilityMutation = this.buildSingleCodingValueMutation(
      BradenScaleFormField.BRADEN_SCALE_MOBILITY_QUESTION,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_MOBILITY,
      false
    )
    if (mobilityMutation) {
      mutations.push(mobilityMutation)
    }

    const nutritionMutation = this.buildSingleCodingValueMutation(
      BradenScaleFormField.BRADEN_SCALE_NUTRITION_QUESTION,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_NUTRITION,
      false
    )
    if (nutritionMutation) {
      mutations.push(nutritionMutation)
    }

    const frictionAndShearMutation = this.buildSingleCodingValueMutation(
      BradenScaleFormField.BRADEN_SCALE_FRICTION_AND_SHEAR_QUESTION,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_FRICTION_AND_SHEAR,
      false
    )
    if (frictionAndShearMutation) {
      mutations.push(frictionAndShearMutation)
    }

    // Build notes field mutations using helper method
    const sensoryNotesMutation = this.buildNotesFieldMutation(
      BradenScaleFormField.BRADEN_SCALE_SENSORY_NOTES,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_SENSORY_NOTES
    )
    if (sensoryNotesMutation) {
      mutations.push(sensoryNotesMutation)
    }

    const moistureNotesMutation = this.buildNotesFieldMutation(
      BradenScaleFormField.BRADEN_SCALE_MOISTURE_NOTES,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_MOISTURE_NOTES
    )
    if (moistureNotesMutation) {
      mutations.push(moistureNotesMutation)
    }

    const activityNotesMutation = this.buildNotesFieldMutation(
      BradenScaleFormField.BRADEN_SCALE_ACTIVITY_NOTES,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_ACTIVITY_NOTES
    )
    if (activityNotesMutation) {
      mutations.push(activityNotesMutation)
    }

    const mobilityNotesMutation = this.buildNotesFieldMutation(
      BradenScaleFormField.BRADEN_SCALE_MOBILITY_NOTES,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_MOBILITY_NOTES
    )
    if (mobilityNotesMutation) {
      mutations.push(mobilityNotesMutation)
    }

    const nutritionNotesMutation = this.buildNotesFieldMutation(
      BradenScaleFormField.BRADEN_SCALE_NUTRITION_NOTES,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_NUTRITION_NOTES
    )
    if (nutritionNotesMutation) {
      mutations.push(nutritionNotesMutation)
    }

    const frictionAndShearNotesMutation = this.buildNotesFieldMutation(
      BradenScaleFormField.BRADEN_SCALE_FRICTION_AND_SHEAR_NOTES,
      PatAssessmentQuestionnaireItem.BRADEN_SCALE_FRICTION_AND_SHEAR_NOTES
    )
    if (frictionAndShearNotesMutation) {
      mutations.push(frictionAndShearNotesMutation)
    }

    return mutations
  }
}
